/* ==========================================================================
   KidStrong B2B Bulk Order Tool
   Native replacement for the InstaOrders app-proxy quick-order page.

   Flow:
     1. Rows come from CSV upload (parsed client-side, free-text SKU) and/or
        manual entry (cascading Product -> Size dropdowns, so manual rows
        always reference a real, known variant — no free-text SKU there).
     2. CSV SKUs are resolved to variants using a JSON map that the Liquid
        section renders server-side at page-load time (script#bulk-order-
        sku-map) — no client-side API call needed for this step. That same
        map is also grouped client-side by product title (preserving the
        source map's own order) to populate the manual-entry dropdowns.
     3. Some rank jerseys (Level 16/17/18 Gold/Platinum/Diamond, currently)
        require a personalization property before this store's sitewide
        ks-customization-guard.liquid script will let /cart/add succeed at
        all — otherwise it silently redirects to the PDP and fakes a 200.
        The SKU map flags this per variant (requiresCustomization /
        customizationLabel, driven by real collection + metafield data, not
        hardcoded here) so this file can require the right field per row.
     4. Every /cart/add.js call is sent ONE ITEM AT A TIME, always — see the
        note at the bottom of this file for why that's no longer optional.
     5. Results render in a per-row status table.

   No backend. No third-party app. No Storefront API token dependency —
   the only network calls this file makes are to Shopify's own AJAX Cart API.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-ks-bulk-order]');
  if (!root) return;

  var fileInput      = root.querySelector('[data-ks-bulk-file-input]');
  var dropzone        = root.querySelector('[data-ks-bulk-dropzone]');
  var fileChipWrap    = root.querySelector('[data-ks-bulk-file-chip]');
  var fileChipName    = root.querySelector('[data-ks-bulk-file-name]');
  var fileClearBtn    = root.querySelector('[data-ks-bulk-file-clear]');
  var manualTableBody = root.querySelector('[data-ks-bulk-manual-body]');
  var addRowBtn       = root.querySelector('[data-ks-bulk-add-row]');
  var submitBtn       = root.querySelector('[data-ks-bulk-submit]');
  var rowCountEl      = root.querySelector('[data-ks-bulk-row-count]');
  var statusEl        = root.querySelector('[data-ks-bulk-status]');
  var statusTextEl    = root.querySelector('[data-ks-bulk-status-text]');
  var resultsWrap     = root.querySelector('[data-ks-bulk-results]');
  var resultsBody     = root.querySelector('[data-ks-bulk-results-body]');
  var summarySuccess  = root.querySelector('[data-ks-bulk-summary-success]');
  var summaryError    = root.querySelector('[data-ks-bulk-summary-error]');
  var viewCartRow     = root.querySelector('[data-ks-bulk-view-cart-row]');

  var csvRows = []; // rows parsed from the most recently uploaded CSV

  /* ---------------------------------------------------------------------
     SKU -> variant map, rendered server-side by the Liquid section into
     a JSON script tag. Keyed lowercase for case-insensitive matching
     (used by the CSV path). Also grouped by product title, in the same
     order the source map yields — groupedProducts feeds the manual-entry
     Product/Size dropdowns. Not re-sorted: relies on JS preserving
     insertion order for the (non-canonical-integer) SKU string keys the
     Liquid map uses, which matches every real SKU format seen on this
     store (e.g. "KS-EQ-1234") — a purely-numeric SKU string would be the
     one edge case where insertion order isn't guaranteed.
     ------------------------------------------------------------------- */
  var skuMap = {};
  var groupedProducts = []; // [{ title, variants: [{ sku, variantId, variantTitle, available, requiresCustomization, customizationLabel }] }]
  (function loadSkuMap() {
    var el = document.getElementById('bulk-order-sku-map');
    if (!el) return;
    try {
      var parsed = JSON.parse(el.textContent || '{}');
      var groupIndexByTitle = {};
      Object.keys(parsed).forEach(function (sku) {
        var entry = parsed[sku];
        skuMap[sku.toLowerCase()] = entry;

        var title = entry.productTitle;
        var idx = groupIndexByTitle[title];
        if (idx === undefined) {
          idx = groupedProducts.length;
          groupIndexByTitle[title] = idx;
          groupedProducts.push({ title: title, variants: [] });
        }
        groupedProducts[idx].variants.push({
          sku: sku,
          variantId: entry.variantId,
          variantTitle: entry.variantTitle,
          available: entry.available,
          requiresCustomization: !!entry.requiresCustomization,
          customizationLabel: entry.customizationLabel || ''
        });
      });
    } catch (e) {
      // Leave skuMap/groupedProducts empty — CSV rows will resolve as
      // "SKU not found" and the manual dropdowns will simply have no
      // products, both visible immediately rather than a silent failure.
      window.console && console.error('Bulk order: could not parse SKU map', e);
    }
  })();

  /* ---------------------------------------------------------------------
     CSV parsing — simple, dependency-free.
     Expected columns (header row required): SKU, Quantity
     Handles basic double-quoted fields; does not attempt full RFC 4180
     edge cases (embedded newlines in quoted fields), which is not a
     realistic need for a two-column SKU/Qty export.
     ------------------------------------------------------------------- */
  function parseCsv(text) {
    var lines = text.split(/\r\n|\r|\n/).filter(function (l) { return l.trim() !== ''; });
    if (!lines.length) return [];

    function splitLine(line) {
      var cells = [];
      var cur = '';
      var inQuotes = false;
      for (var i = 0; i < line.length; i++) {
        var ch = line[i];
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === ',' && !inQuotes) {
          cells.push(cur.trim());
          cur = '';
        } else {
          cur += ch;
        }
      }
      cells.push(cur.trim());
      return cells;
    }

    var header = splitLine(lines[0]).map(function (h) { return h.toLowerCase().replace(/[^a-z0-9]/g, ''); });
    var skuIdx = header.indexOf('sku');
    var qtyIdx = header.indexOf('quantity');
    if (qtyIdx === -1) qtyIdx = header.indexOf('qty');

    var startIdx = 0;
    if (skuIdx !== -1 && qtyIdx !== -1) {
      startIdx = 1; // real header row found, skip it
    } else {
      // No recognizable header — assume column order is SKU, Quantity
      skuIdx = 0;
      qtyIdx = 1;
    }

    var rows = [];
    for (var i = startIdx; i < lines.length; i++) {
      var cells = splitLine(lines[i]);
      var sku = (cells[skuIdx] || '').trim();
      var qtyRaw = (cells[qtyIdx] || '').trim();
      if (!sku && !qtyRaw) continue;
      rows.push({ sku: sku, qtyRaw: qtyRaw, source: 'csv' });
    }
    return rows;
  }

  function handleFile(file) {
    if (!file) return;
    var reader = new FileReader();
    reader.onload = function (e) {
      csvRows = parseCsv(String(e.target.result || ''));
      fileChipName.textContent = file.name + ' — ' + csvRows.length + ' row' + (csvRows.length === 1 ? '' : 's');
      fileChipWrap.style.display = 'inline-flex';
      updateRowCount();
    };
    reader.onerror = function () {
      alert('Could not read that file. Please re-export as a plain CSV and try again.');
    };
    reader.readAsText(file);
  }

  if (fileInput) {
    fileInput.addEventListener('change', function () {
      handleFile(fileInput.files && fileInput.files[0]);
    });
  }
  if (dropzone) {
    dropzone.addEventListener('click', function () { fileInput && fileInput.click(); });
    ['dragenter', 'dragover'].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        dropzone.classList.add('is-dragover');
      });
    });
    ['dragleave', 'drop'].forEach(function (evt) {
      dropzone.addEventListener(evt, function (e) {
        e.preventDefault();
        dropzone.classList.remove('is-dragover');
      });
    });
    dropzone.addEventListener('drop', function (e) {
      var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) handleFile(file);
    });
  }
  if (fileClearBtn) {
    fileClearBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      csvRows = [];
      if (fileInput) fileInput.value = '';
      fileChipWrap.style.display = 'none';
      updateRowCount();
    });
  }

  /* ---------------------------------------------------------------------
     Manual entry rows — cascading Product -> Size dropdowns, then an
     optional Personalization field, then Qty.

     Selecting a product populates the size dropdown from that product's
     own variants (groupedProducts, built above); the size option itself
     carries variantId/sku/variantTitle/available/requiresCustomization/
     customizationLabel needed to add to cart, so no lookup is needed
     later — a manual row always references a real, known variant once
     both dropdowns are filled in.

     When the resolved variant requires customization (per the sitewide
     guard's own collection + metafield — see top-of-file comment), the
     Personalization input for that row is revealed and required, and the
     Quantity input is locked to 1 (one row = one name = one unit).
     ------------------------------------------------------------------- */
  function populateProductSelect(select) {
    var placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select product…';
    select.appendChild(placeholder);
    groupedProducts.forEach(function (group, idx) {
      var opt = document.createElement('option');
      opt.value = String(idx);
      opt.textContent = group.title;
      select.appendChild(opt);
    });
  }

  function populateSizeSelect(sizeSelect, groupIndexRaw) {
    sizeSelect.innerHTML = '';
    var placeholder = document.createElement('option');
    placeholder.value = '';

    if (groupIndexRaw === '' || groupIndexRaw == null) {
      placeholder.textContent = 'Select product first';
      sizeSelect.appendChild(placeholder);
      sizeSelect.disabled = true;
      return;
    }

    var group = groupedProducts[parseInt(groupIndexRaw, 10)];
    placeholder.textContent = 'Select size…';
    sizeSelect.appendChild(placeholder);
    sizeSelect.disabled = false;

    if (!group) return;
    group.variants.forEach(function (v) {
      var opt = document.createElement('option');
      opt.value = v.variantId;
      opt.textContent = (v.variantTitle && v.variantTitle !== 'Default Title') ? v.variantTitle : 'One Size';
      opt.dataset.sku = v.sku;
      opt.dataset.variantTitle = v.variantTitle || '';
      opt.dataset.available = v.available ? 'true' : 'false';
      opt.dataset.requiresCustomization = v.requiresCustomization ? 'true' : 'false';
      opt.dataset.customizationLabel = v.customizationLabel || '';
      sizeSelect.appendChild(opt);
    });
  }

  function toggleCustomField(tr, requiresCustom, label) {
    var customInput = tr.querySelector('[data-ks-bulk-manual-custom]');
    var qtyInput = tr.querySelector('[data-ks-bulk-manual-qty]');
    if (!customInput) return;
    if (requiresCustom) {
      customInput.style.display = '';
      customInput.placeholder = label || 'Personalization';
      customInput.setAttribute('aria-label', label || 'Personalization');
      customInput.required = true;
      if (qtyInput) {
        qtyInput.value = '1';
        qtyInput.disabled = true;
      }
    } else {
      customInput.style.display = 'none';
      customInput.value = '';
      customInput.required = false;
      if (qtyInput) qtyInput.disabled = false;
    }
  }

  function makeManualRow() {
    var tr = document.createElement('tr');
    tr.innerHTML =
      '<td class="ks-bulk-row-product"><select class="ks-bulk-input" data-ks-bulk-manual-product></select></td>' +
      '<td class="ks-bulk-row-size"><select class="ks-bulk-input" data-ks-bulk-manual-size disabled></select></td>' +
      '<td class="ks-bulk-row-custom"><input type="text" class="ks-bulk-input" data-ks-bulk-manual-custom style="display:none" placeholder=""></td>' +
      '<td class="ks-bulk-row-qty"><input type="number" class="ks-bulk-input" data-ks-bulk-manual-qty min="1" step="1" placeholder="Qty"></td>' +
      '<td><button type="button" class="ks-bulk-row-remove" data-ks-bulk-remove-row aria-label="Remove row">' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>' +
      '</button></td>';

    var productSelect = tr.querySelector('[data-ks-bulk-manual-product]');
    var sizeSelect     = tr.querySelector('[data-ks-bulk-manual-size]');
    populateProductSelect(productSelect);
    populateSizeSelect(sizeSelect, null);

    productSelect.addEventListener('change', function () {
      populateSizeSelect(sizeSelect, productSelect.value);
      toggleCustomField(tr, false, ''); // reset until a size is (re-)chosen
      updateRowCount();
    });
    sizeSelect.addEventListener('change', function () {
      var opt = sizeSelect.selectedOptions[0];
      var requiresCustom = !!(opt && opt.dataset.requiresCustomization === 'true');
      var label = opt ? opt.dataset.customizationLabel : '';
      toggleCustomField(tr, requiresCustom, label);
      updateRowCount();
    });

    return tr;
  }

  function addManualRow() {
    if (!manualTableBody) return;
    var row = makeManualRow();
    manualTableBody.appendChild(row);
    var productSelect = row.querySelector('[data-ks-bulk-manual-product]');
    if (productSelect) productSelect.focus();
  }

  if (addRowBtn) addRowBtn.addEventListener('click', addManualRow);

  if (manualTableBody) {
    // Seed with a single empty row to start; "+ Add Row" adds more as needed
    addManualRow();

    manualTableBody.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-ks-bulk-remove-row]');
      if (!btn) return;
      var tr = btn.closest('tr');
      if (tr) tr.remove();
      updateRowCount();
    });
    manualTableBody.addEventListener('input', updateRowCount);
  }

  function getManualRows() {
    if (!manualTableBody) return [];
    var rows = [];
    Array.from(manualTableBody.querySelectorAll('tr')).forEach(function (tr) {
      var productSelect = tr.querySelector('[data-ks-bulk-manual-product]');
      var sizeSelect     = tr.querySelector('[data-ks-bulk-manual-size]');
      var customInput    = tr.querySelector('[data-ks-bulk-manual-custom]');
      var qtyInput       = tr.querySelector('[data-ks-bulk-manual-qty]');

      var productVal = productSelect ? productSelect.value : '';
      var qtyVal     = qtyInput ? qtyInput.value.trim() : '';
      var sizeOpt    = (sizeSelect && sizeSelect.selectedOptions) ? sizeSelect.selectedOptions[0] : null;
      var sizeVal    = sizeOpt ? sizeOpt.value : '';

      if (!productVal && !qtyVal) return; // fully blank row, skip

      var row = {
        source: 'manual', qtyRaw: qtyVal, sku: null, variantId: null,
        productTitle: null, variantTitle: null, available: null,
        requiresCustomization: false, customizationLabel: '', customizationValue: ''
      };
      if (productVal !== '' && sizeVal !== '') {
        var group = groupedProducts[parseInt(productVal, 10)];
        row.variantId              = sizeVal;
        row.sku                    = sizeOpt.dataset.sku;
        row.variantTitle           = sizeOpt.dataset.variantTitle;
        row.available              = sizeOpt.dataset.available === 'true';
        row.requiresCustomization  = sizeOpt.dataset.requiresCustomization === 'true';
        row.customizationLabel     = sizeOpt.dataset.customizationLabel || '';
        row.productTitle           = group ? group.title : '';
        row.customizationValue     = customInput ? customInput.value.trim() : '';
      }
      rows.push(row);
    });
    return rows;
  }

  // Both row sources already exclude fully-blank rows at construction time
  // (parseCsv skips lines with no SKU and no qty; getManualRows skips rows
  // with neither a product nor a qty chosen) — no need to re-filter here.
  // Re-filtering on `r.sku || r.qtyRaw` specifically would have silently
  // dropped a manual row where only a product was picked (sku is null
  // until a size is also chosen), so intentionally not doing that.
  function getAllRows() {
    return csvRows.concat(getManualRows());
  }

  function updateRowCount() {
    var count = getAllRows().length;
    if (rowCountEl) rowCountEl.textContent = count + ' row' + (count === 1 ? '' : 's') + ' ready';
    if (submitBtn) submitBtn.disabled = count === 0;
  }
  updateRowCount();

  /* ---------------------------------------------------------------------
     Cart error classification
     Maps Shopify's plain-text /cart/add.js error strings to the status
     vocabulary the results table wants. Falls back to the raw message
     when nothing matches — we never invent an error Shopify didn't give us.
     ------------------------------------------------------------------- */
  function classifyCartError(message) {
    var msg = message || 'Could not add to cart';
    if (/minimum of (\d+)/i.test(msg) || /at least (\d+)/i.test(msg)) {
      return { status: 'Below minimum order quantity', detail: msg };
    }
    if (/maximum/i.test(msg) || /up to (\d+)/i.test(msg)) {
      return { status: 'Exceeds maximum quantity', detail: msg };
    }
    if (/multiple of (\d+)/i.test(msg) || /increments? of (\d+)/i.test(msg)) {
      return { status: 'Must order in increments', detail: msg };
    }
    if (/only \d+ (left|available)/i.test(msg) || /not enough/i.test(msg) || /out of stock/i.test(msg) || /sold out/i.test(msg)) {
      return { status: 'Insufficient inventory', detail: msg };
    }
    return { status: 'Cart error', detail: msg };
  }

  /* ---------------------------------------------------------------------
     Cart add — always one item per request. See the note at the bottom of
     this file for why batching was removed entirely (not just de-prioritized).
     ------------------------------------------------------------------- */
  function cartAddOne(item) {
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [item] })
    }).then(function (res) {
      return res.json().then(function (data) { return { ok: res.ok, data: data }; });
    });
  }

  function cartAddSequential(rows, onEach) {
    var i = 0;
    function next() {
      if (i >= rows.length) return Promise.resolve();
      var row = rows[i];
      var item = { id: row.variantId, quantity: row.quantity };
      if (row.properties) item.properties = row.properties;
      return cartAddOne(item)
        .then(function (result) {
          onEach(row, result);
          i++;
          return next();
        });
    }
    return next();
  }

  /* ---------------------------------------------------------------------
     Results table rendering
     ------------------------------------------------------------------- */
  function badgeHtml(label, kind) {
    return '<span class="ks-bulk-badge ks-bulk-badge--' + kind + '">' + label + '</span>';
  }

  function classifyStatusKind(status) {
    if (status === 'success') return 'success';
    if (status === 'SKU not found') return 'error';
    if (status === 'Invalid quantity') return 'warn';
    if (status === 'Invalid selection') return 'warn';
    if (/required$/.test(status)) return 'warn';       // e.g. "Member Last Name required"
    if (/^Requires /.test(status)) return 'warn';       // e.g. "Requires Member Last Name"
    return 'error';
  }

  function renderResults(rows) {
    resultsBody.innerHTML = '';
    var successCount = 0, errorCount = 0;

    rows.forEach(function (row) {
      var tr = document.createElement('tr');
      var kind = classifyStatusKind(row.status);
      var label = row.status === 'success' ? 'Added to cart' : (row.status || 'Error');
      if (kind === 'success') { successCount++; } else { errorCount++; }
      var badge = badgeHtml(label, kind);

      tr.innerHTML =
        '<td class="ks-bulk-sku-cell">' + escapeHtml(row.sku || '—') + '</td>' +
        '<td>' + escapeHtml(String(row.quantity != null ? row.quantity : row.qtyRaw || '—')) + '</td>' +
        '<td class="ks-bulk-muted-cell">' + escapeHtml(row.productTitle || '—') + '</td>' +
        '<td>' + badge + (row.detail ? '<div class="ks-bulk-muted-cell" style="margin-top:4px;font-size:0.78rem;">' + escapeHtml(row.detail) + '</div>' : '') + '</td>';
      resultsBody.appendChild(tr);
    });

    if (summarySuccess) summarySuccess.textContent = successCount + ' added';
    if (summaryError) summaryError.textContent = errorCount + ' need attention';
    if (viewCartRow) viewCartRow.style.display = successCount > 0 ? 'block' : 'none';
    resultsWrap.classList.add('is-visible');
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function setStatus(text) {
    if (!statusEl) return;
    if (text) {
      statusEl.classList.add('is-active');
      statusTextEl.textContent = text;
    } else {
      statusEl.classList.remove('is-active');
    }
  }

  /* ---------------------------------------------------------------------
     Main submit flow
     ------------------------------------------------------------------- */
  function submit() {
    var rawRows = getAllRows();
    if (!rawRows.length) return;

    submitBtn.disabled = true;
    resultsWrap.classList.remove('is-visible');

    // Validate quantity, then resolve each row. CSV rows resolve against the
    // server-rendered SKU map (can come back "SKU not found", or rejected
    // outright if they require personalization — those must be re-entered
    // manually so the customer can supply the name). Manual rows already
    // carry a real variantId straight from the dropdowns they were built
    // from, so "SKU not found" never applies to them.
    var rows = rawRows.map(function (r) {
      var out = { sku: r.sku, qtyRaw: r.qtyRaw, quantity: null, status: null, productTitle: r.productTitle };

      // Rows requiring personalization are always exactly qty 1 (one row =
      // one name = one unit) — enforced here regardless of what the
      // (normally disabled/locked) quantity field shows, as a second layer
      // on top of the UI lock.
      var forceQtyOne = r.source === 'manual' && r.requiresCustomization;
      var qty = forceQtyOne ? 1 : parseInt(r.qtyRaw, 10);

      if (!forceQtyOne && (!qty || qty <= 0 || String(qty) !== String(r.qtyRaw).trim())) {
        out.status = 'Invalid quantity';
        out.detail = 'Enter a whole number greater than 0.';
        return out;
      }
      out.quantity = qty;

      if (r.source === 'manual') {
        if (!r.variantId) {
          out.status = 'Invalid selection';
          out.detail = 'Select a product and size for this row.';
          return out;
        }
        if (r.requiresCustomization && !r.customizationValue) {
          var label = r.customizationLabel || 'Personalization';
          out.status = label + ' required';
          out.detail = 'Enter the ' + label + ' for this item before adding it to your cart.';
          out.productTitle = r.productTitle;
          return out;
        }
        if (r.available === false) {
          out.status = 'Insufficient inventory';
          out.detail = 'This variant is currently unavailable.';
        }
        out.variantId = r.variantId;
        out.productTitle = r.productTitle + (r.variantTitle && r.variantTitle !== 'Default Title' ? ' — ' + r.variantTitle : '');
        if (r.requiresCustomization) {
          out.properties = {};
          out.properties[r.customizationLabel] = r.customizationValue;
        }
        return out;
      }

      // CSV path — free-text SKU, resolved against the server-rendered map.
      var sku = (r.sku || '').trim();
      if (!sku) {
        out.status = 'SKU not found';
        out.detail = 'No SKU entered on this row.';
        return out;
      }
      var match = skuMap[sku.toLowerCase()];
      if (!match) {
        out.status = 'SKU not found';
        out.detail = 'No matching product/variant for SKU "' + sku + '" in this tool’s catalog.';
        return out;
      }
      out.productTitle = match.productTitle + (match.variantTitle && match.variantTitle !== 'Default Title' ? ' — ' + match.variantTitle : '');
      if (match.requiresCustomization) {
        // Can't collect the personalization value from a CSV row — this
        // store's sitewide cart guard will reject (silently redirect) any
        // /cart/add for this variant without it, so don't even try.
        out.status = 'Requires ' + match.customizationLabel;
        out.detail = 'Requires ' + match.customizationLabel + ' — add this item manually below instead.';
        return out;
      }
      if (!match.available) {
        out.status = 'Insufficient inventory';
        out.detail = 'This variant is currently unavailable.';
      }
      out.variantId = match.variantId;
      return out;
    });

    var toAdd = rows.filter(function (r) { return !r.status && r.variantId; });

    if (!toAdd.length) {
      renderResults(rows);
      submitBtn.disabled = false;
      return;
    }

    setStatus('Adding ' + toAdd.length + ' item' + (toAdd.length === 1 ? '' : 's') + ' to your cart, one at a time…');

    cartAddSequential(toAdd, function (row, res) {
      if (res.ok) {
        row.status = 'success';
      } else {
        var msg = (res.data && (res.data.description || res.data.message)) || 'Could not add to cart';
        var classified = classifyCartError(msg);
        row.status = classified.status;
        row.detail = classified.detail;
      }
    }).then(function () {
      renderResults(rows);
      setStatus(null);
      submitBtn.disabled = false;
      document.dispatchEvent(new CustomEvent('cart:build'));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: {} }));
    });
  }

  if (submitBtn) submitBtn.addEventListener('click', submit);

})();

/* ==========================================================================
   Why every /cart/add.js call is sent one item at a time, always (no more
   batch-first fast path):

   This store runs a sitewide script (snippets/ks-customization-guard.liquid,
   rendered in theme.liquid) that intercepts every /cart/add call and blocks
   adding certain variants (currently 3 rank jerseys requiring a "Member
   Last Name" property) unless a matching cart line property is present —
   otherwise it silently redirects to the PDP and fakes a 200 {} response.

   That guard only inspects items[0] of a batched { items: [...] } payload —
   it does not loop over every item in a multi-item array. If a
   customization-required item without its property ever landed anywhere
   in a batch, the result would be unpredictable: a faked-success silently
   swallowing the WHOLE batch if it was item[0], or slipping through
   unchecked if it wasn't. Sending exactly one item per request sidesteps
   that bug entirely and guarantees the guard (and Shopify's own per-item
   validation) evaluates every row correctly, which is what keeps this
   tool's results table accurate. This was previously a "try batch, fall
   back to sequential on failure" optimization for speed; given the guard's
   behavior, correctness matters more than that minor speed gain, so
   sequential-only is now the only path, not just a fallback.
   ========================================================================== */
